import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { ChangeVertical } from '../components/ChangeVertical';
import { ChipCount } from '../components/ChipCount';
import { chargedText } from '../components/Workspace';
import type { PrActionBody, PrDetailBody, ReviewItem, ReviewQueueBody } from '../../shared/pr/api';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/dates';
import { personName } from '../lib/people';
import { REVIEW_SORTS, reviewList, type ReviewSort } from '../../shared/pr/review-list';

// Procurement review inbox (screen 04, P1.4). Decisions go to the broker, which applies the
// status rules (no self-approval, remarks required) and records them; buttons here are a guide.

type Queue = 'ProcurementReview' | 'WithDeptHead';
type Load<T> =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: T };

const ERRORS = new Set([
  'self_approval',
  'reason_required',
  'invalid_transition',
  'conflict',
  'no_dept_head',
  'forbidden',
]);
const errKey = (code: string) =>
  (ERRORS.has(code) ? `review.error.${code}` : 'review.error.generic') as MessageKey;

function failed(err: unknown): { kind: 'error'; code: string; reference: string | null } {
  const e = err instanceof ApiError ? err : null;
  return { kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null };
}

function daysSince(iso: string, now = Date.now()): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : Math.max(0, Math.floor((now - t) / 86_400_000));
}

export function ReviewPage({ focus }: { focus?: string | undefined } = {}) {
  const { t } = useI18n();
  const [queue, setQueue] = useState<Queue>('ProcurementReview');
  const [office, setOffice] = useState('');
  const [list, setList] = useState<Load<ReviewQueueBody>>({ kind: 'loading' });
  const [selected, setSelected] = useState<string | null>(focus ?? null);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<ReviewSort>('newest');
  // A request opened from the queue (#/review/<no>, D118): looked for in both inboxes once.
  const [pending, setPending] = useState<string | null>(focus ?? null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    let live = true;
    setList({ kind: 'loading' });
    const q = new URLSearchParams({ status: queue, ...(office ? { office } : {}) });
    apiGet<ReviewQueueBody>(`/api/review?${q.toString()}`)
      .then((data) => {
        if (!live) return;
        setList({ kind: 'ok', data });
        if (pending && !data.items.some((i) => i.prNo === pending)) {
          if (queue === 'ProcurementReview') {
            setQueue('WithDeptHead');
            return;
          }
          setPending(null);
        }
        setSelected((s) =>
          s && data.items.some((i) => i.prNo === s)
            ? s
            : (reviewList(data.items, '', 'newest')[0]?.prNo ?? null),
        );
      })
      .catch((err: unknown) => live && setList(failed(err)));
    return () => {
      live = false;
    };
  }, [queue, office, reload]);

  const offices = list.kind === 'ok' ? list.data.offices : [];
  const items = list.kind === 'ok' ? list.data.items : [];
  const shown = useMemo(() => reviewList(items, query, sort), [items, query, sort]);

  // Bring the chosen request into view inside the list (it scrolls on its own, D118).
  useEffect(() => {
    if (!pending || list.kind !== 'ok') return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-pr="${pending}"]`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
      setPending(null);
    }
  }, [pending, list, shown]);

  return (
    <div class="stack">
      <p class="caption">{t('review.intro')}</p>
      <div class="review-bar">
        <div class="admin-tabs" role="group" aria-label={t('review.queues')}>
          {(['ProcurementReview', 'WithDeptHead'] as const).map((q) => (
            <button
              key={q}
              type="button"
              class="chip"
              aria-pressed={q === queue}
              onClick={() => {
                setQueue(q);
                setNotice(null);
                setPending(null);
              }}
            >
              {t(`review.queue.${q}` as MessageKey)}
              {q === queue && list.kind === 'ok' && <ChipCount n={items.length} />}
            </button>
          ))}
        </div>
        <div class="field review-search">
          <label for="review-search">{t('review.search')}</label>
          <input
            id="review-search"
            type="search"
            value={query}
            placeholder={t('review.searchHint')}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field review-office">
          <label for="review-sort">{t('queue.sort')}</label>
          <select
            id="review-sort"
            value={sort}
            onChange={(e) => setSort((e.target as HTMLSelectElement).value as ReviewSort)}
          >
            {REVIEW_SORTS.map((s) => (
              <option key={s} value={s}>
                {t(`review.sort.${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
        <div class="field review-office">
          <label for="review-office">{t('review.office')}</label>
          <select
            id="review-office"
            value={office}
            onChange={(e) => setOffice((e.target as HTMLSelectElement).value)}
          >
            <option value="">{t('review.allOffices')}</option>
            {offices.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>
      {notice && (
        <p class="form-saved" role="status">
          {notice}
        </p>
      )}
      {list.kind === 'loading' && (
        <section class="card" aria-busy="true">
          <p>{t('req.loading')}</p>
        </section>
      )}
      {list.kind === 'error' && (
        <section class="card card--error" role="alert">
          <p>{t(list.code === 'forbidden' ? 'review.error.forbidden' : 'req.loadError')}</p>
          {list.reference && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: list.reference })}
            </p>
          )}
        </section>
      )}
      {list.kind === 'ok' &&
        (items.length === 0 ? (
          <section class="card">
            <p>{t('review.empty')}</p>
          </section>
        ) : (
          <div class="review-layout">
            <div class="review-side">
              {query.trim() && (
                <p class="caption" role="status">
                  {t('review.found', { n: String(shown.length), total: String(items.length) })}
                </p>
              )}
              {shown.length === 0 ? (
                <p class="card">{t('review.noMatch')}</p>
              ) : (
                <ul
                  ref={listRef}
                  class="review-list"
                  aria-label={t(`review.queue.${queue}` as MessageKey)}
                >
                  {shown.map((item) => (
                    <QueueItem
                      key={item.prNo}
                      item={item}
                      selected={item.prNo === selected}
                      onSelect={() => {
                        setSelected(item.prNo);
                        setNotice(null);
                      }}
                    />
                  ))}
                </ul>
              )}
            </div>
            {selected && (
              <ReviewPanel
                key={`${selected}-${reload}`}
                prNo={selected}
                onDone={(message) => {
                  setNotice(message);
                  setReload((r) => r + 1);
                }}
              />
            )}
          </div>
        ))}
    </div>
  );
}

const MONEY = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const money = (n: number) => MONEY.format(n);
/** Decisions shown before "Show full history" (D81). */
const RECENT = 2;

function QueueItem(props: { item: ReviewItem; selected: boolean; onSelect: () => void }) {
  const { t, lang } = useI18n();
  const { item, selected, onSelect } = props;
  const days = daysSince(item.created);
  return (
    <li data-pr={item.prNo}>
      <button
        type="button"
        class={`review-item${selected ? ' review-item--selected' : ''}`}
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
      >
        <span class="review-item__top">
          <span class="review-item__ref" dir="ltr">
            {item.prNo}
          </span>
          <span class="caption">
            {days === 0
              ? t('review.today')
              : days === 1
                ? t('review.day')
                : t('review.days', { n: String(days) })}
          </span>
        </span>
        <span class="review-item__title">{item.title}</span>
        {item.created && (
          <span class="caption" dir="ltr">
            {t('review.received', { when: formatDateTime(item.created, lang) })}
          </span>
        )}
        <span class="caption">
          {item.requesterName} · {item.department} · {item.location || item.office}
        </span>
        <span class="caption">
          {[
            item.entity,
            item.estBudget !== null ? `${item.currency} ${money(item.estBudget)}` : '',
            item.neededBy ? t('review.neededBy', { date: formatDate(item.neededBy, lang) }) : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span class="review-item__tags">
          {item.rush && <span class="pill pill--warning">{t('req.rush')}</span>}
          {item.own && <span class="pill pill--neutral">{t('review.yours')}</span>}
          {item.deptHeadAsked && <span class="pill pill--info">{t('review.headAsked')}</span>}
        </span>
      </button>
    </li>
  );
}

function ReviewPanel({ prNo, onDone }: { prNo: string; onDone: (message: string) => void }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load<PrDetailBody>>({ kind: 'loading' });
  const [askHead, setAskHead] = useState(false);
  const [headReason, setHeadReason] = useState('');
  const [remark, setRemark] = useState('');
  const [office, setOffice] = useState('');
  const [officeReason, setOfficeReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [offices, setOffices] = useState<string[]>([]);
  const [allHistory, setAllHistory] = useState(false);

  useEffect(() => {
    let live = true;
    apiGet<PrDetailBody>(`/api/prs/${encodeURIComponent(prNo)}/detail`)
      .then((data) => {
        if (!live) return;
        setLoad({ kind: 'ok', data });
        setOffice(data.office);
      })
      .catch((err: unknown) => live && setLoad(failed(err)));
    apiGet<ReviewQueueBody>('/api/review')
      .then((q) => live && setOffices(q.offices))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [prNo]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{t('req.loadError')}</p>
      </section>
    );
  }
  const pr = load.data;
  const newestFirst = [...pr.decisions].sort(
    (a, b) => b.when.localeCompare(a.when) || b.step - a.step,
  );
  const category = pr.category
    ? lang === 'ar'
      ? pr.category.nameAR || pr.category.nameEN
      : pr.category.nameEN
    : '';
  const can = (a: PrDetailBody['actions'][number]) => pr.actions.includes(a);
  const own = pr.status === 'ProcurementReview' && !can('approve') && can('reject');

  async function act(body: Record<string, unknown>, message: MessageKey) {
    setBusy(true);
    setProblem(null);
    try {
      await apiSend<PrActionBody>('POST', `/api/prs/${encodeURIComponent(prNo)}/action`, {
        etag: pr.etag,
        ...body,
      });
      onDone(t(message, { no: prNo }));
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setProblem(
        t(errKey(e?.code ?? 'generic')) +
          (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
      );
    } finally {
      setBusy(false);
    }
  }

  const needRemark = (action: 'return' | 'reject') => {
    if (remark.trim()) return void act({ action, reason: remark }, `review.done.${action}`);
    setProblem(t('review.error.reason_required'));
  };

  return (
    <section class="card stack review-panel" aria-labelledby="review-title">
      <div class="review-panel__head">
        <div>
          <p class="review-ref" dir="ltr">
            {pr.prNo}
          </p>
          <h2 id="review-title" class="detail-title" dir="auto">
            {pr.title}
          </h2>
        </div>
        {pr.estBudget !== null && (
          <div class="review-panel__budget">
            <span class="caption">{t('review.budget')}</span>
            <span class="tile__value ref" dir="ltr">
              {pr.currency} {money(pr.estBudget)}
            </span>
          </div>
        )}
      </div>
      <dl class="facts facts--compact">
        {(
          [
            ['req.f.requester', pr.requesterName || '—'],
            ['req.f.department', pr.department || '—'],
            ['req.f.chargedTo', chargedText(pr, t)],
            ['req.f.location', pr.location ? `${pr.location} (${pr.office})` : pr.office || '—'],
            ['pr.profile.entity', pr.entity || '—', true],
            ['req.f.category', category || '—'],
            ['req.f.neededBy', pr.neededBy ? formatDate(pr.neededBy, lang) : '—'],
            ['req.f.raised', pr.created ? formatDateTime(pr.created, lang) : '—', true],
          ] as [MessageKey, string, boolean?][]
        ).map(([k, v, ltr]) => (
          <div key={k}>
            <dt>{t(k)}</dt>
            <dd dir={ltr ? 'ltr' : undefined}>{v}</dd>
          </div>
        ))}
      </dl>
      <div class="review-why">
        <p class="facts__label">{t('req.f.justification')}</p>
        <p dir="auto">{pr.justification}</p>
        {pr.rush && (
          <>
            <p class="facts__label">{t('req.f.rushReason')}</p>
            <p dir="auto">{pr.rushReason}</p>
          </>
        )}
      </div>
      <ul class="review-lines">
        {pr.lines.map((l) => (
          <li key={l.lineNo}>
            <span dir="auto">
              {l.qty} × {l.description}
              {l.specs && <span class="caption"> · {l.specs}</span>}
            </span>
            <span class="caption">
              {l.qty} {t(`pr.unit.${l.unit}` as MessageKey)}
            </span>
          </li>
        ))}
      </ul>
      {pr.decisions.length > 0 && (
        <div>
          <p class="facts__label">
            {t(allHistory ? 'review.history.all' : 'review.history.recent')}
          </p>
          <ol class="timeline">
            {(allHistory ? newestFirst : newestFirst.slice(0, RECENT)).map((d) => (
              <li key={d.step}>
                <span class="timeline__what">{t(`decision.${d.decision}` as MessageKey)}</span>
                {d.remark && <span dir="auto">{d.remark}</span>}
                <span class="caption">
                  <span title={d.by}>{personName(d.by)}</span>
                  {d.when && ` · ${formatDateTime(d.when, lang)}`}
                </span>
              </li>
            ))}
          </ol>
          {newestFirst.length > RECENT && (
            <button
              type="button"
              class="button button--small review-history__toggle"
              aria-expanded={allHistory}
              onClick={() => setAllHistory(!allHistory)}
            >
              {allHistory
                ? t('review.history.less')
                : t('review.history.more', { n: String(newestFirst.length) })}
            </button>
          )}
        </div>
      )}
      {pr.files.length > 0 && (
        <p class="caption">
          {t('req.files')}: {pr.files.join(', ')}
        </p>
      )}
      <a class="caption" href={`#/requests/${pr.prNo}`}>
        {t('review.openFull')}
      </a>

      {pr.status === 'WithDeptHead' && <p class="notice notice--info">{t('review.withHead')}</p>}

      {pr.actions.length > 0 && (
        <div class="stack">
          {own && <p class="notice notice--info">{t('review.ownNotice')}</p>}
          {problem && (
            <p class="form-problem" role="alert">
              {problem}
            </p>
          )}
          {can('sendToDeptHead') && (
            <fieldset class="review-head">
              <legend>{t('review.head.title')}</legend>
              <div class="admin-tabs">
                <button
                  type="button"
                  class="chip"
                  aria-pressed={!askHead}
                  onClick={() => setAskHead(false)}
                >
                  {t('review.head.no')}
                </button>
                <button
                  type="button"
                  class="chip"
                  aria-pressed={askHead}
                  onClick={() => setAskHead(true)}
                >
                  {t('review.head.ask')}
                </button>
              </div>
              {askHead && (
                <div class="field">
                  <label for="review-head-reason">{t('review.head.reason')}</label>
                  <input
                    id="review-head-reason"
                    type="text"
                    maxLength={2000}
                    value={headReason}
                    onInput={(e) => setHeadReason((e.target as HTMLInputElement).value)}
                  />
                  <p class="caption">{t('review.head.hint')}</p>
                </div>
              )}
            </fieldset>
          )}
          <div class="field">
            <label for="review-remark">{t('review.remark')}</label>
            <textarea
              id="review-remark"
              rows={3}
              maxLength={2000}
              placeholder={t('review.remarkHint')}
              value={remark}
              onInput={(e) => setRemark((e.target as HTMLTextAreaElement).value)}
            />
          </div>
          <div class="actions review-actions">
            {can('reject') && (
              <button
                type="button"
                class="button button--danger"
                disabled={busy}
                onClick={() => needRemark('reject')}
              >
                {t('review.reject')}
              </button>
            )}
            {can('return') && (
              <button
                type="button"
                class="button"
                disabled={busy}
                onClick={() => needRemark('return')}
              >
                {t('review.return')}
              </button>
            )}
            {askHead && can('sendToDeptHead') ? (
              <button
                type="button"
                class="button button--primary"
                disabled={busy}
                onClick={() => {
                  if (!headReason.trim()) return setProblem(t('review.error.reason_required'));
                  void act(
                    { action: 'sendToDeptHead', reason: headReason },
                    'review.done.sendToDeptHead',
                  );
                }}
              >
                {t('review.sendToHead')}
              </button>
            ) : (
              can('approve') && (
                <button
                  type="button"
                  class="button button--primary"
                  disabled={busy}
                  onClick={() => void act({ action: 'approve' }, 'review.done.approve')}
                >
                  {t('review.approve')}
                </button>
              )
            )}
          </div>
        </div>
      )}

      {pr.canChangeOffice && (
        <details class="review-office-change">
          <summary>{t('review.office.change')}</summary>
          <div class="admin-form__grid">
            <div class="field">
              <label for="review-new-office">{t('review.office')}</label>
              <select
                id="review-new-office"
                value={office}
                onChange={(e) => setOffice((e.target as HTMLSelectElement).value)}
              >
                {offices.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div class="field">
              <label for="review-office-reason">{t('review.office.reason')}</label>
              <input
                id="review-office-reason"
                type="text"
                maxLength={2000}
                value={officeReason}
                onInput={(e) => setOfficeReason((e.target as HTMLInputElement).value)}
              />
            </div>
          </div>
          <button
            type="button"
            class="button"
            disabled={busy || office === pr.office}
            onClick={() => {
              if (!officeReason.trim()) return setProblem(t('review.error.reason_required'));
              void act(
                { action: 'changeOffice', office, reason: officeReason },
                'review.done.changeOffice',
              );
            }}
          >
            {t('review.office.save')}
          </button>
        </details>
      )}
      <ChangeVertical pr={pr} onDone={onDone} />
    </section>
  );
}
